// client/src/components/pages/Settings/LexiStarDisplayRules/displayRulesLogic.ts
//
// Pure logic of Settings → LexiStar → Map colours & states (ST-00276 phase 2). The editor works
// on the rule list itself (map/displayRules DisplayRule[]); this module gives it:
//   * the builder's form of a condition (a flat list of rows, matched all / any — the notification
//     rule builder's path / op / value rows, plus active-event rows), and back;
//   * a new rule of each layer, unique ids, moving a rule up / down within its layer;
//   * the field picker's groups — only the fields the organisation's devices report (the
//     catalogue's `available`), plus a field the rule already uses;
//   * the live preview: the draft evaluated over the organisation's trucks now (counts per colour,
//     the trucks whose display would change);
//   * the colour picker's choices: each colour once, by its colour name.

import {
  DISPLAY_TOKENS,
  EVENT_CATALOGUE,
  LIST_OPS,
  NO_VALUE_OPS,
  OP_TEXT,
  compileRules,
  evaluateDisplay,
  fieldLabel,
  normaliseCondition,
  opsForField,
  type AssetDisplay,
  type Condition,
  type ConditionOp,
  type DisplayLayer,
  type DisplayRule,
  type DisplayToken,
  type EventCondition,
  type FieldCondition,
  type Scalar,
} from '../../../map/displayRules';
import { ASSET_STATUS, type AssetStatusKey } from '../../../map/assetVisualRules';
import type { CatalogueField, DisplayCatalogue, DisplayPreviewAsset } from '../../../../types/lexistar-display-rules';
import { eventTypeLabel } from '../../../../utils/eventTypeLabels';

// ── The colour picker ───────────────────────────────────────────────────────────────────────
// A display token is a status style, and several statuses share a colour: To source and
// Travelling empty are both sky blue. Offering the twelve tokens by their status words read as
// picking another status (ST-00276 review, 2026-10-29), so the picker offers each colour once,
// by its colour name. Picking a colour stores the first token of that colour, unless the rule
// already holds a token of that colour: that one is kept, so the rule's words and map key stay.

export interface ColourChoice {
  /** The token stored when this colour is picked. */
  token: DisplayToken;
  /** Every token drawn in this colour, `token` first. */
  tokens: DisplayToken[];
  name: string;
}

const COLOUR_NAMES: Partial<Record<DisplayToken, string>> = {
  to_source: 'Sky blue',
  at_source: 'Green',
  loaded: 'Navy',
  delay: 'Orange',
  parked: 'Grey',
  maintenance: 'Purple',
  breakdown: 'Red',
  offline: 'Grey outline',
};

/** What a token looks like: its map colour, and hollow (dashed) or not. */
const lookOf = (t: DisplayToken): string => {
  const s = ASSET_STATUS[t as AssetStatusKey];
  return `${s.marker}${s.hollow ? ' hollow' : ''}`;
};

/** Each colour once, in palette order. */
export const COLOUR_CHOICES: readonly ColourChoice[] = (() => {
  const byLook = new Map<string, ColourChoice>();
  for (const t of DISPLAY_TOKENS) {
    const choice = byLook.get(lookOf(t));
    if (choice) choice.tokens.push(t);
    else byLook.set(lookOf(t), { token: t, tokens: [t], name: COLOUR_NAMES[t] ?? ASSET_STATUS[t as AssetStatusKey].marker });
  }
  return [...byLook.values()];
})();

/** The colour a rule's token is drawn in (null for none). */
export const colourChoiceOf = (token: DisplayToken | undefined): ColourChoice | null =>
  (token && COLOUR_CHOICES.find((c) => c.tokens.includes(token))) || null;

/** The token to store when `choice` is picked for a rule that holds `current`. */
export const tokenForColour = (choice: ColourChoice, current: DisplayToken | undefined): DisplayToken =>
  current && choice.tokens.includes(current) ? current : choice.token;

// ── Conditions in the builder's form ────────────────────────────────────────────────────────

export type Leaf = FieldCondition | EventCondition;
export interface SimpleCondition {
  /** How the rows combine. */
  mode: 'all' | 'any';
  /** No rows: always. */
  rows: Leaf[];
}

const isLeaf = (c: Condition): c is Leaf => 'path' in (c as object) || 'event' in (c as object);
export const isEventLeaf = (c: Leaf): c is EventCondition => 'event' in c;

/** The builder's form of a condition, or null when it nests deeper than one all / any of rows. */
export function simpleOf(when: Condition): SimpleCondition | null {
  const c = normaliseCondition(when) as Condition;
  if ('always' in (c as object)) return { mode: 'all', rows: [] };
  if (isLeaf(c)) return { mode: 'all', rows: [c] };
  for (const mode of ['all', 'any'] as const) {
    if (mode in (c as object)) {
      const list = (c as Record<string, Condition[]>)[mode] as Condition[];
      return list.every(isLeaf) ? { mode, rows: list as Leaf[] } : null;
    }
  }
  return null;
}

/** The condition a builder form stands for. */
export function whenOf(s: SimpleCondition): Condition {
  if (s.rows.length === 0) return { always: true };
  if (s.rows.length === 1) return s.rows[0] as Leaf;
  return s.mode === 'all' ? { all: s.rows } : { any: s.rows };
}

/** A new row on a field (its first operator; an empty value). */
export function newRow(fieldId: string): Leaf {
  if (fieldId.startsWith('event:')) return { event: fieldId.slice(6), within_s: 7200 };
  const op = opsForField(fieldId)[0] as ConditionOp;
  return LIST_OPS.includes(op) ? { path: fieldId, op, value: [] } : { path: fieldId, op, value: '' as Scalar };
}

/** The picker value of a row: the field id, or `event:<TYPE>`. */
export const rowField = (r: Leaf): string => (isEventLeaf(r) ? `event:${r.event}` : r.path);

/** A row with another operator (the value kept when it still fits). */
export function withOp(r: FieldCondition, op: ConditionOp): FieldCondition {
  if (NO_VALUE_OPS.includes(op)) return { path: r.path, op };
  const list = LIST_OPS.includes(op);
  const v = r.value;
  if (list) return { path: r.path, op, value: Array.isArray(v) ? v : v === undefined || v === '' ? [] : [v] };
  return { path: r.path, op, value: Array.isArray(v) ? (v[0] ?? '') : (v ?? '') };
}

/** A typed value from the editor's text for a field kind. */
export function parseValue(kind: CatalogueField['kind'] | undefined, text: string): Scalar {
  const s = text.trim();
  if (kind === 'number') return s !== '' && Number.isFinite(Number(s)) ? Number(s) : s;
  if (kind === 'boolean') return s === 'true' ? true : s === 'false' ? false : s;
  return s;
}

/** An event window in words and back (minutes / hours / days). */
export type WindowUnit = 'min' | 'h' | 'd';
export const WINDOW_SECONDS: Record<WindowUnit, number> = { min: 60, h: 3600, d: 86400 };
export function windowParts(withinS: number): { amount: number; unit: WindowUnit } {
  if (withinS % 86400 === 0) return { amount: withinS / 86400, unit: 'd' };
  if (withinS % 3600 === 0) return { amount: withinS / 3600, unit: 'h' };
  return { amount: Math.round(withinS / 60), unit: 'min' };
}

// ── Rules ───────────────────────────────────────────────────────────────────────────────────

/** A rule id from words (unique among `taken`). */
export function ruleIdFor(words: string, taken: ReadonlySet<string>): string {
  const base =
    words
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 48) || 'rule';
  let id = /^[a-z0-9]/.test(base) ? base : `r-${base}`;
  let n = 2;
  while (taken.has(id)) id = `${base.slice(0, 44)}-${n++}`;
  return id;
}

/**
 * A new rule of a layer, ready to edit. A colour rule starts with one empty condition on
 * `fieldId` (it cannot be saved, and matches nothing, until the value is given).
 */
export function newRule(layer: DisplayLayer, taken: ReadonlySet<string>, fieldId = 'reading.fuel_level_pct'): DisplayRule {
  if (layer === 'base') {
    return { id: ruleIdFor('my-colour', taken), name: 'My colour rule', layer: 'base', when: newRow(fieldId), visual: { key: 'breakdown' } };
  }
  return {
    id: ruleIdFor('my-mark', taken),
    name: 'My mark',
    layer: 'overlay',
    priority: 20,
    when: { event: 'FUEL.LOSS_SUSPECTED', within_s: 7200 },
    visual: { mark: 'pulse', key: 'breakdown' },
  };
}

/**
 * Insert a new rule: a colour rule at the top of the colours (the organisation's own rule is
 * meant to win — move it down to let a standard one win first); a mark last.
 */
export function insertRule(rules: readonly DisplayRule[], rule: DisplayRule): DisplayRule[] {
  if (rule.layer === 'overlay') return [...rules, rule];
  const firstBase = rules.findIndex((r) => r.layer === 'base');
  const at = firstBase === -1 ? 0 : firstBase;
  return [...rules.slice(0, at), rule, ...rules.slice(at)];
}

/** Move a rule one place up (-1) or down (+1) among the rules of its layer. */
export function moveRule(rules: readonly DisplayRule[], id: string, dir: -1 | 1): DisplayRule[] {
  const i = rules.findIndex((r) => r.id === id);
  if (i === -1) return [...rules];
  const layer = rules[i]!.layer;
  let j = i + dir;
  while (j >= 0 && j < rules.length && rules[j]!.layer !== layer) j += dir;
  if (j < 0 || j >= rules.length) return [...rules];
  const out = [...rules];
  [out[i], out[j]] = [out[j]!, out[i]!];
  return out;
}

export const replaceRule = (rules: readonly DisplayRule[], id: string, next: DisplayRule): DisplayRule[] =>
  rules.map((r) => (r.id === id ? next : r));

export const removeRule = (rules: readonly DisplayRule[], id: string): DisplayRule[] => rules.filter((r) => r.id !== id);

// ── The field picker ─────────────────────────────────────────────────────────────────────────

export interface FieldChoice {
  value: string;
  label: string;
  disabled?: boolean;
}
export interface FieldChoiceGroup {
  label: string;
  options: FieldChoice[];
}

/**
 * The picker's groups: the catalogue's available fields by group, then the active events seen, and
 * — so a rule never loses its field — `current` when it is not offered (marked "not reported").
 */
export function fieldChoices(catalogue: DisplayCatalogue | null, current: string | null = null): FieldChoiceGroup[] {
  const groups: FieldChoiceGroup[] = [];
  const offered = new Set<string>();
  if (catalogue) {
    for (const g of catalogue.groups) {
      const options = catalogue.fields
        .filter((f) => f.group === g.id && f.available)
        .map((f) => ({ value: f.id, label: f.unit ? `${f.label} (${f.unit})` : f.label }));
      options.forEach((o) => offered.add(o.value));
      if (options.length) groups.push({ label: g.label, options });
    }
    const events = catalogue.events.filter((e) => e.available).map((e) => ({ value: `event:${e.type}`, label: e.label }));
    events.forEach((o) => offered.add(o.value));
    if (events.length) groups.push({ label: 'Recent events', options: events });
  }
  if (current && !offered.has(current)) {
    const label = current.startsWith('event:')
      ? (EVENT_CATALOGUE.find((e) => e.value === current.slice(6))?.label ?? eventTypeLabel(current.slice(6)))
      : fieldLabel(current);
    groups.push({ label: 'In this rule', options: [{ value: current, label: `${label} (not reported now)` }] });
  }
  return groups;
}

/** The catalogue entry of a field id (null for an event or an unknown one). */
export const fieldOf = (catalogue: DisplayCatalogue | null, id: string): CatalogueField | null =>
  catalogue?.fields.find((f) => f.id === id) ?? null;

/** The operators the builder offers for a row's field, in words. */
export const opChoices = (fieldId: string): Array<{ value: ConditionOp; label: string }> =>
  opsForField(fieldId).map((op) => ({ value: op, label: OP_TEXT[op] }));

// ── The live preview ─────────────────────────────────────────────────────────────────────────

export interface PreviewResult {
  /** Trucks per colour token under the draft (palette order, only tokens in use). */
  counts: Array<{ token: DisplayToken; n: number }>;
  /** Trucks per mark rule under the draft. */
  marks: Array<{ rule_id: string; label: string; n: number }>;
  /** Trucks whose colour or marks change from what they show now. */
  changed: Array<{ asset: DisplayPreviewAsset; before: AssetDisplay; after: AssetDisplay }>;
  total: number;
}

const markIds = (d: AssetDisplay) => d.overlays.map((o) => o.rule_id).join(',');

/** The draft over the organisation's trucks now. */
export function previewOf(
  rules: readonly DisplayRule[],
  assets: readonly DisplayPreviewAsset[],
  nowMs: number = Date.now()
): PreviewResult {
  const compiled = compileRules(rules, -1);
  const counts = new Map<DisplayToken, number>();
  const marks = new Map<string, { label: string; n: number }>();
  const changed: PreviewResult['changed'] = [];
  for (const a of assets) {
    const after = evaluateDisplay(compiled, a.facts, a.events, nowMs);
    counts.set(after.base.token, (counts.get(after.base.token) || 0) + 1);
    for (const o of after.overlays) {
      const m = marks.get(o.rule_id) || { label: o.label || o.rule_id, n: 0 };
      m.n++;
      marks.set(o.rule_id, m);
    }
    const before = a.display;
    if (before && (before.base.token !== after.base.token || before.base.label !== after.base.label || markIds(before) !== markIds(after))) {
      changed.push({ asset: a, before, after });
    }
  }
  return {
    counts: DISPLAY_TOKENS.filter((t) => counts.has(t)).map((t) => ({ token: t, n: counts.get(t) as number })),
    marks: [...marks.entries()].map(([rule_id, m]) => ({ rule_id, label: m.label, n: m.n })),
    changed,
    total: assets.length,
  };
}
